import { useAuth } from "@/contexts/AuthContext";
import { useSelection } from "@/contexts/SelectionContext";
import { useDeleteLessons } from "@/lib/hooks/lessons/useDeleteLessons";
import { useDeleteProjects } from "@/lib/hooks/projects/useDeleteProjects";
import { useDeleteSlides } from "@/lib/hooks/slides/useDeleteSlides";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { type ErrorCode, ErrorCodes } from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";

interface DeleteModalProps {
  isOpen: boolean;
}

const SUPERUSER_OVERRIDABLE_ERRORS: ErrorCode[] = [
  ErrorCodes.CANNOT_DELETE_WITH_ACTIVE_CHILDREN,
  ErrorCodes.CANNOT_DELETE_ORIGINAL_WITH_LINKED_COPIES,
];

const OVERRIDE_MESSAGES: Record<string, string> = {
  [ErrorCodes.CANNOT_DELETE_WITH_ACTIVE_CHILDREN]:
    "This item has non-deleted children. Are you sure ?",
  [ErrorCodes.CANNOT_DELETE_ORIGINAL_WITH_LINKED_COPIES]:
    "This item has linked copies. Are you sure ?",
};

export function DeleteModal({ isOpen }: DeleteModalProps) {
  const {
    selectedProjectIds,
    selectedLessonIds,
    selectedSlideIds,
    clearAllSelections,
    toggleDeleteModal,
    toggleIsSelectionMode,
  } = useSelection();

  const { user } = useAuth();

  const [errorMessage, setErrorMessage] = useState<{
    code: ErrorCode | null;
    message: string;
  } | null>(null);

  const {
    deleteLessons,
    loading: deleteLessonsLoading,
    error: deleteLessonsError,
  } = useDeleteLessons();
  const {
    deleteProjects,
    loading: deleteProjectsLoading,
    error: deleteProjectsError,
  } = useDeleteProjects();
  const {
    deleteSlides,
    loading: deleteSlidesLoading,
    error: deleteSlidesError,
  } = useDeleteSlides();

  const handleClose = () => {
    const totalItems = selectedLessonIds.length + selectedProjectIds.length;
    if (totalItems === 1) {
      clearAllSelections();
    } else {
      toggleIsSelectionMode(true);
    }
    setErrorMessage(null);
    toggleDeleteModal(false);
  };

  const handleDelete = async (skipCheck: boolean) => {
    try {
      setErrorMessage(null);

      if (selectedLessonIds.length > 0) {
        await deleteLessons(selectedLessonIds, skipCheck);
      }

      if (selectedProjectIds.length > 0) {
        await deleteProjects(selectedProjectIds, skipCheck);
      }

      if (selectedSlideIds.length > 0) {
        await deleteSlides(selectedSlideIds, skipCheck);
      }

      clearAllSelections();
      toggleDeleteModal(false);
    } catch (error) {
      for (const code of SUPERUSER_OVERRIDABLE_ERRORS) {
        if (hasGraphQLErrorCode(error, code)) {
          setErrorMessage({ code, message: (error as Error).message });
          return;
        }
      }

      setErrorMessage({
        code: null,
        message: "Failed to delete item. Please try again.",
      });
    }
  };

  const selectedCount =
    selectedLessonIds.length + selectedProjectIds.length + selectedSlideIds.length;

  const isLoading = deleteLessonsLoading || deleteProjectsLoading || deleteSlidesLoading;
  const isError = !!(deleteLessonsError || deleteProjectsError || deleteSlidesError);
  const isSuperuser = user?.role && ["super", "superuser"].includes(user?.role);

  const canOverride =
    isSuperuser && errorMessage?.code && SUPERUSER_OVERRIDABLE_ERRORS.includes(errorMessage.code);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={"Warning!"} size="sm" style="danger">
      <div className="flex flex-col">{renderContent()}</div>
    </Modal>
  );

  function renderContent() {
    if (!errorMessage?.code) {
      return (
        <>
          <p className="text-secondary-200 mb-6 text-center">
            {selectedCount > 1
              ? `${selectedCount} items will be permanently deleted.`
              : "This item will be permanently deleted."}{" "}
            Linked copies will be unlinked.
          </p>
          <div className="flex gap-3 justify-between">
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(false)}
              variant="danger"
              disabled={isLoading || isError}
            >
              Delete
            </Button>
          </div>
        </>
      );
    }

    if (canOverride) {
      return (
        <>
          <p className="text-secondary-200 mb-6 text-center">
            {OVERRIDE_MESSAGES[errorMessage.code] ?? errorMessage.message}
          </p>
          <div className="flex justify-between">
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button onClick={() => handleDelete(true)} variant="danger">
              Continue
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <p className="text-secondary-200 mb-6 text-center">{errorMessage.message}</p>
        <div className="flex justify-center">
          <Button onClick={handleClose} variant="secondary">
            Close
          </Button>
        </div>
      </>
    );
  }
}
